import React, { useEffect, useState } from "react";
import axios from "axios";
import backIcon from "../icons/back_icn.png";
import styles from "./AfterPayment.module.css";

const AfterPayment = ({ status, onBack }) => {
  const [currentStatus, setCurrentStatus] = useState(status);
  const [loading, setLoading] = useState(!status);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fullName, setFullName] = useState("");
  const [ticketGenerated, setTicketGenerated] = useState(false);
  const [ticketUrl, setTicketUrl] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    if (!status && isAuthenticated) {
      const fetchStatus = async () => {
        const params = new URLSearchParams(window.location.search);
        const fetchedOrderId = params.get('orderId');
        if (fetchedOrderId) {
          setOrderId(fetchedOrderId);
          try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`/api/payments/status?orderId=${encodeURIComponent(fetchedOrderId)}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            setCurrentStatus(response.data.status);
            if (response.data.downloadUrl) {
              setDownloadUrl(response.data.downloadUrl);
            }

            window.history.replaceState(null, '', window.location.pathname);
          } catch (error) {
            setCurrentStatus('error');
            window.history.replaceState(null, '', window.location.pathname);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      };

      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [status, isAuthenticated]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleGenerateTicket = async () => {
    if (!fullName.trim()) {
      setMessage({ text: "Введи повне ім'я для квитка.", type: "error" });
      return;
    }

    if (!orderId) {
      setMessage({ text: "Не знайшли orderId. Напиши в підтримку.", type: "error" });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post('/api/tickets/generate-ticket', {
        orderId,
        fullName,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.ticketUrl) {
        setTicketUrl(response.data.ticketUrl);
        setTicketGenerated(true);
	setMessage({ text: "Квиток згенеровано успішно!", type: "success" });
      } else {
        setMessage({ text: "Не вдалося згенерувати квиток. Спробуй пізніше.", type: "error" });
      }
    } catch (error) {
      setMessage({ text: "Помилка при генерації квитка. Спробуй пізніше.", type: "error" });
    }
  };

  const handleDownloadTicket = async () => {
    if (!ticketUrl) {
      setMessage({ text: "Посилання на квиток недоступне.", type: "error" });
      return;
    }

    try {
      setDownloadProgress(0);
      const token = localStorage.getItem('authToken');

      const response = await axios.get(ticketUrl, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
	onDownloadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setDownloadProgress(percent);
       },
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ticket.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      window.URL.revokeObjectURL(downloadUrl);

      setMessage({ text: "Квиток завантажено!", type: "success" });
    } catch (error) {
      setMessage({ text: "Не вийшло завантажити квиток. Спробуй пізніше.", type: "error" });
    } finally {
    setDownloadProgress(null); 
   }
  };

  const handleDownloadAsset = async () => {
  if (!downloadUrl) {
    setMessage({ text: "Посилання на файл недоступне.", type: "error" });
    return;
  }

  try {
    setDownloadProgress(0);
    const token = localStorage.getItem('authToken');

    const response = await axios.get(downloadUrl, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      onDownloadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setDownloadProgress(percent);
      },
    });

    let filename = '';
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.includes('filename=')) {
      const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch != null && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    } else {
      const urlParts = downloadUrl.split('/');
      filename = urlParts[urlParts.length - 1];
    }

    if (!filename) {
      filename = 'Beat';
    }

    const blob = new Blob([response.data]);
    const downloadUrlBlob = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrlBlob;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(downloadUrlBlob);

    setMessage({ text: "Файл завантажено!", type: "success" });
  } catch {
    setMessage({ text: "Не вдалося завантажити файл. Спробуй пізніше.", type: "error" });
  } finally {
    setDownloadProgress(null);
  }
};

  if (loading) {
    return (
      <div className={styles.afterPaymentContainer}>
        <div className={styles.afterPaymentContent}>Loading payment status...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.afterPaymentContainer}>
        <div className={styles.afterPaymentContent}>
          <h2>Please Log In</h2>
          <p>You need to be logged in to access this page.</p>
        </div>
      </div>
    );
  }

  let content;

  switch (currentStatus) {
    case "success":
      content = (
        <div className={styles.success}>
          <h2>Оплата пройшла успішно!</h2>
          <p>Дякуємо за покупку.</p>
          <p>Згенеруй електронний квиток і завантаж його PDF. Ти можеш зробити це пізніше у розділі "Мої Квитки"</p>

          {!ticketGenerated ? (
            <div className={styles.actionsContainer}>
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  placeholder="Ваше повне ім'я"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={styles.inputField}
                />
              </div>
              <div className={styles.buttonsContainer}>
                <button onClick={handleGenerateTicket} className={styles.generateButton}>
                  Згенерувати квиток
                </button>
                {downloadUrl && (
                  <button onClick={handleDownloadAsset} className={styles.downloadButton}>
                    Завантажити Файл
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.buttonsContainer}>
              {ticketUrl && (
                <button onClick={handleDownloadTicket} className={styles.downloadButton}>
                  Завантажити квиток
                </button>
              )}
              {downloadUrl && (
                <button onClick={handleDownloadAsset} className={styles.downloadButton}>
                  Завантажити Файл
                </button>
              )}
            </div>
          )}
        </div>
      );
      break;

    case "failed":
      content = (
        <div className={`${styles.failed}`}>
          <h2>Оплата не пройшла</h2>
          <p>Спробуй ще раз або звернися в підтримку.</p>
        </div>
      );
      break;
    case "error":
      content = (
        <div className={`${styles.error}`}>
          <h2>Payment Error</h2>
          <p>We hit a snag processing your payment.</p>
        </div>
      );
      break;
    default:
      content = (
        <div className={`${styles.unknown}`}>
          <h2>Unknown Payment Status</h2>
          <p>
            We couldn't figure out your payment status. Please reach out to support.
          </p>
        </div>
      );
  }

  return (
    <div className={styles.afterPaymentContainer}>
      <img
        src={backIcon}
        alt="Back"
        className={`${styles.backIcon} ${styles.visible}`}
        onClick={onBack}
      />
      <div className={styles.afterPaymentContent}>
        {content}
      </div>
      {message && (
        <div className={styles.overlay}>
          <div className={`${styles.overlayMessage} ${styles[messageType]}`}>
            {message.text}
          </div>
        </div>
      )}
      {downloadProgress !== null && (
      <div className={styles.progressOverlay}>
        <div className={styles.progressText}>{downloadProgress}%</div>
      </div>
    )}
    </div>
  );
};

export default AfterPayment;

